import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnonAnalysisView } from "../Components/Analysis/AnonView";
import { getUserName } from "../DataTransform";
import { useQuery } from "@apollo/client";
import { ResultsResult, GET_RESULTS } from "../Queries";
import { ThinLayout } from "../Layouts/ThinLayout";
import { ResultsPreviewBanner } from "../Components/ResultsPreviewBanner";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useTranslation } from "react-i18next";

export const ResultsPreview = () => {
  const { campaignId, userId, source } = useParams();
  const navigate = useNavigate();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();

  //
  // State
  //

  const [authorType, setAuthorType] = useState<string>("");

  //
  // Queries
  //

  const { loading: resultsLoading, data: resultsData } =
    useQuery<ResultsResult>(GET_RESULTS, {
      variables: {
        id: campaignId,
        userId,
        authorType: authorType || undefined,
      },
      fetchPolicy: "network-only", // Doesn't check cache before making a network request
      onError: (error) => setError(t("errors.results.list"), error),
    });

  const results = resultsData ? resultsData.results : undefined;

  const showLoading = resultsLoading;

  const campaign = results?.campaign;
  const user = results?.user;
  const analyses = results?.analyses;
  const aggregates = results?.aggregates;
  const overall = results?.overall;
  const template = results?.form;
  const hasMixedAuthors = results?.hasMixedAuthors;
  const hasMultipleRaterTypes = results?.hasMultipleRaterTypes ?? false;
  const hideOverallAverages = campaign?.course?.hideOverallAverages ?? false;

  const title = `${user ? getUserName(user) : "Your"} results for ${
    campaign?.name
  }`;

  const returnUrl =
    source == "campaign"
      ? `/campaigns/${campaignId}`
      : `/results/${campaignId}//${user?.id}/`;

  return (
    <ThinLayout title={title} loading={showLoading}>
      {/* FL-312: shared blue preview banner replaces the purple
          secondary-title bar; the previewed student's name is kept. */}
      {user && (
        <ResultsPreviewBanner
          message={`Previewing results as ${getUserName(user)}`}
          onClose={() => navigate(returnUrl)}
        />
      )}
      {template && campaign && user && analyses && aggregates && overall && (
        <AnonAnalysisView
          title={title}
          campaign={campaign}
          user={user}
          analyses={analyses}
          aggregates={aggregates}
          overall={overall}
          template={template}
          preview={true}
          hasMixedAuthors={hasMixedAuthors}
          hasMultipleRaterTypes={hasMultipleRaterTypes}
          authorType={authorType}
          onAuthorTypeChange={(v) => setAuthorType(v)}
          hideOverallAverages={hideOverallAverages}
        />
      )}
    </ThinLayout>
  );
};
